import { useEffect, useRef, useState } from "react";
import { Redirect, useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isClarityOneLocalDevEnabled } from "@/lib/clarityOneMode";
import { sanitizeClarityOneRedirect } from "@shared/clarityOneAuthRedirect";
import { CLARITY_ONE_OTP_RESEND_COOLDOWN_SECONDS } from "@shared/clarityOneCustomerAccess";

type Step = "phone" | "otp";

export default function ClarityOneLoginPage() {
  const search = useSearch();
  const redirectAfterLogin = sanitizeClarityOneRedirect(new URLSearchParams(search).get("redirect"));
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [phoneMasked, setPhoneMasked] = useState<string | null>(null);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);
  const requestInFlightRef = useRef(false);

  const localDev = isClarityOneLocalDevEnabled();
  const { data: devBypassActive } = trpc.clarityOneAuth.devLoginBypassActive.useQuery(undefined, {
    enabled: localDev,
    refetchOnWindowFocus: false,
  });

  const { data: me, isSuccess } = trpc.clarityOneAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (resendCooldownSec <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldownSec((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldownSec]);

  if (isSuccess && me) {
    const dest = redirectAfterLogin === "/" ? "/home" : redirectAfterLogin;
    return <Redirect to={dest} />;
  }

  const applyOtpRequestSuccess = (data: {
    message: string;
    devOtp?: string | null;
    phoneMasked?: string | null;
    retryAfterSeconds?: number | null;
  }) => {
    setStep("otp");
    setDevOtp(data.devOtp ?? null);
    setPhoneMasked(data.phoneMasked ?? null);
    setResendCooldownSec(
      typeof data.retryAfterSeconds === "number" && data.retryAfterSeconds > 0
        ? data.retryAfterSeconds
        : CLARITY_ONE_OTP_RESEND_COOLDOWN_SECONDS,
    );
    if (data.devOtp) {
      setOtp(data.devOtp);
      console.info("[Clarity One DEV] OTP:", data.devOtp);
    }
    toast.success(data.message);
  };

  const requestOtp = trpc.clarityOneAuth.requestOtp.useMutation({
    onSuccess: (data) => {
      applyOtpRequestSuccess(data);
    },
    onError: (err) => toast.error(err.message),
    onSettled: () => {
      requestInFlightRef.current = false;
    },
  });

  const verifyOtp = trpc.clarityOneAuth.verifyOtp.useMutation({
    onSuccess: () => {
      const dest = redirectAfterLogin === "/" ? "/home" : redirectAfterLogin;
      window.location.href = dest;
    },
    onError: (err) => toast.error(err.message),
  });

  const submitOtpRequest = (phoneValue: string) => {
    const trimmed = phoneValue.trim();
    if (!trimmed) return;
    if (requestInFlightRef.current || requestOtp.isPending) return;
    if (step === "otp" && resendCooldownSec > 0) return;
    requestInFlightRef.current = true;
    requestOtp.mutate({ phone: trimmed });
  };

  const handlePhoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitOtpRequest(phone);
  };

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = otp.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) return;
    verifyOtp.mutate({ phone: phone.trim(), code });
  };

  const resetFlow = () => {
    setStep("phone");
    setOtp("");
    setDevOtp(null);
    setPhoneMasked(null);
    setResendCooldownSec(0);
    requestInFlightRef.current = false;
  };

  const resendDisabled =
    requestOtp.isPending || requestInFlightRef.current || resendCooldownSec > 0;

  return (
    <ClarityOneLayout showNav={false}>
      <div className="max-w-md mx-auto pt-4 sm:pt-8 pb-6 text-center">
        <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-[#1A2744] mb-3">Welcome to Clarity One</h1>
        <p className="text-sm text-[#8B7355] leading-relaxed">
          Your personal client portal. Sign in with the mobile number registered with Clarity.
        </p>
        {localDev && devBypassActive && (
          <p className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Local dev mode — verification codes appear below instead of WhatsApp.
          </p>
        )}
      </div>

      <div className="max-w-md mx-auto mt-8 bg-white rounded-2xl border border-[#E8E4DC] p-6 sm:p-8 shadow-sm">
        {step === "otp" ? (
          <form onSubmit={handleOtpSubmit} className="space-y-5">
            <div className="text-center mb-2">
              <p className="text-[#1A2744] font-medium mb-1">Enter verification code</p>
              <p className="text-sm text-[#8B7355] leading-relaxed">
                {phoneMasked
                  ? `If this number is registered, a verification code was sent to ${phoneMasked}.`
                  : "Verification code sent if the mobile number is registered."}
              </p>
            </div>

            {devOtp && (
              <div className="text-center text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Dev OTP: <span className="font-mono font-semibold">{devOtp}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="otp" className="text-[#1A2744] text-sm">
                6-digit code
              </Label>
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="h-12 text-center text-xl tracking-[0.3em] font-mono border-[#E8E4DC] focus-visible:ring-[#B8956E]"
                autoComplete="one-time-code"
                autoFocus
              />
            </div>

            <Button
              type="submit"
              disabled={verifyOtp.isPending || otp.replace(/\D/g, "").length !== 6}
              className="w-full h-12 bg-[#1A2744] hover:bg-[#243556] text-white rounded-xl text-base"
            >
              {verifyOtp.isPending ? "Verifying…" : "Sign in"}
            </Button>

            <div className="flex flex-col gap-2 text-center">
              <button
                type="button"
                className="text-sm text-[#B8956E] hover:underline disabled:opacity-50 disabled:no-underline"
                disabled={resendDisabled}
                onClick={() => submitOtpRequest(phone)}
                data-testid="clarity-one-otp-resend"
              >
                {requestOtp.isPending
                  ? "Sending…"
                  : resendCooldownSec > 0
                    ? `Resend code in ${resendCooldownSec}s`
                    : "Resend code"}
              </button>
              <button
                type="button"
                className="text-sm text-[#8B7355] hover:underline"
                onClick={resetFlow}
                data-testid="clarity-one-change-phone"
              >
                Use a different number
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handlePhoneSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#1A2744] text-sm">
                Mobile number
              </Label>
              <Input
                id="phone"
                type="tel"
                inputMode="tel"
                placeholder="9123 4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-12 text-base border-[#E8E4DC] focus-visible:ring-[#B8956E]"
                autoComplete="tel"
                autoFocus
              />
              <p className="text-xs text-[#8B7355]">Include the country code for non-Singapore numbers</p>
            </div>
            <Button
              type="submit"
              disabled={requestOtp.isPending || requestInFlightRef.current || !phone.trim()}
              className="w-full h-12 bg-[#1A2744] hover:bg-[#243556] text-white rounded-xl text-base"
            >
              {requestOtp.isPending
                ? "Sending code…"
                : devBypassActive
                  ? "Send verification code (dev)"
                  : "Send verification code via WhatsApp"}
            </Button>
          </form>
        )}
      </div>

      <p className="max-w-md mx-auto text-center text-xs text-[#8B7355] mt-8 leading-relaxed px-4">
        Clarity One is available exclusively to existing Clarity clients with portal access enabled.
        <br />
        Need help? Contact your Clarity representative.
      </p>
    </ClarityOneLayout>
  );
}
